import { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/Navbar";
import { Calendar as CalendarIcon, Users, Clock, BookOpen, ArrowRight } from "lucide-react";
import { format, parseISO, isSameDay } from "date-fns";
import { trackEvent } from "@/lib/track";
import { Seo } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SchedulePage() {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [filterSubject, setFilterSubject] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/classes?upcoming=true`),
      axios.get(`${API}/subjects`),
    ]).then(([classRes, subRes]) => {
      setClasses(classRes.data);
      setSubjects(subRes.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return classes.filter((c) => !filterSubject || c.subject_id === filterSubject);
  }, [classes, filterSubject]);

  const datesWithClasses = useMemo(() => {
    const set = new Set(filtered.map((c) => c.date));
    return [...set].map((d) => {
      const [y, m, day] = d.split("-").map(Number);
      return new Date(y, m - 1, day);
    });
  }, [filtered]);

  const visibleClasses = useMemo(() => {
    if (!selectedDate) return filtered;
    return filtered.filter((c) => isSameDay(parseISO(c.date), selectedDate));
  }, [filtered, selectedDate]);

  // Group by date for the indefinite scrollable list
  const groupedByDate = useMemo(() => {
    const groups = {};
    visibleClasses.forEach((c) => {
      if (!groups[c.date]) groups[c.date] = [];
      groups[c.date].push(c);
    });
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [visibleClasses]);

  return (
    <div className="min-h-screen bg-white dark:bg-[#0f1117] transition-colors duration-300" data-testid="schedule-page">
      <Seo
        title="Class Timetable | Small Group English Classes Melbourne"
        rawTitle
        description="See upcoming small group English class times for Year 7-12 in Melbourne. Book a single class with credits or enrol for the term."
        path="/schedule"
      />
      <Navbar />

      {/* Page header */}
      <section className="hero-gradient dark:bg-transparent pt-28 pb-10 px-6">
        <div className="max-w-7xl mx-auto text-center animate-fade-in-up">
          <Badge className="bg-brand-purple-soft text-brand-purple mb-4 font-body font-semibold px-4 py-1.5 text-sm rounded-full">
            Book a Class
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight mb-4">
            Upcoming Class Schedule
          </h1>
          <p className="text-base md:text-lg text-slate-600 dark:text-slate-400 font-body max-w-2xl mx-auto">
            Browse every upcoming group class. Click a highlighted date to filter, or scroll the full list below.
          </p>
        </div>
      </section>

      <section className="py-12 px-6 bg-white dark:bg-[#0f1117] transition-colors duration-300" data-testid="schedule-section">
        <div className="max-w-7xl mx-auto">
          {/* Subject filter */}
          {subjects.length > 1 && (
            <div className="flex flex-wrap gap-2 justify-center mb-8" data-testid="subject-filter">
              <Button size="sm" variant={!filterSubject ? "default" : "outline"} onClick={() => setFilterSubject("")}
                className={`rounded-full font-body ${!filterSubject ? "bg-brand-purple hover:bg-brand-purple-hover text-white" : "border-slate-200 text-slate-600 hover:border-brand-purple hover:text-brand-purple"}`}
                data-testid="filter-all">
                All Subjects
              </Button>
              {subjects.map((s) => (
                <Button key={s.id} size="sm" variant={filterSubject === s.id ? "default" : "outline"} onClick={() => setFilterSubject(s.id)}
                  className={`rounded-full font-body ${filterSubject === s.id ? "bg-brand-purple hover:bg-brand-purple-hover text-white" : "border-slate-200 text-slate-600 hover:border-brand-purple hover:text-brand-purple"}`}
                  data-testid={`filter-subject-${s.id}`}>
                  {s.name}
                </Button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Calendar */}
            <div className="lg:col-span-4">
              <div className="bg-white dark:bg-[#1a1b23] rounded-2xl border border-slate-100 dark:border-slate-800 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-4 transition-colors duration-300" data-testid="schedule-calendar">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(d) => setSelectedDate(d || null)}
                  modifiers={{ hasClass: datesWithClasses }}
                  modifiersStyles={{
                    hasClass: { backgroundColor: '#F3E9F8', color: '#6B2D8B', fontWeight: '600', borderRadius: '8px' }
                  }}
                  className="w-full"
                />
                <div className="flex items-center justify-between mt-3 px-3 pb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-brand-purple-soft border border-brand-purple/20"></div>
                    <span className="text-xs text-slate-500 font-body">Classes available</span>
                  </div>
                  {selectedDate && (
                    <Button size="sm" variant="ghost" onClick={() => setSelectedDate(null)}
                      className="text-xs text-brand-purple hover:bg-brand-purple-soft font-body h-auto py-1 px-2"
                      data-testid="clear-date-filter">
                      Clear filter
                    </Button>
                  )}
                </div>
              </div>

              <div className="mt-6 bg-brand-purple-soft/50 dark:bg-brand-purple/10 rounded-2xl p-5 border border-brand-purple/10">
                <h4 className="font-heading text-base font-bold text-brand-purple dark:text-brand-purple-light mb-2">New to Tutors Melbourne?</h4>
                <p className="text-xs text-slate-600 dark:text-slate-300 font-body leading-relaxed mb-3">
                  Not sure which class is right? Browse our subjects to see what's covered in each course.
                </p>
                <Link to="/pricing" data-testid="schedule-info-classes-link">
                  <Button size="sm" variant="outline" className="rounded-full font-body text-xs border-brand-purple text-brand-purple hover:bg-brand-purple hover:text-white">
                    Browse Classes <ArrowRight className="ml-1 h-3 w-3" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Upcoming list */}
            <div className="lg:col-span-8" data-testid="class-list">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-xl font-semibold text-slate-900 dark:text-white">
                  {selectedDate
                    ? format(selectedDate, "EEEE, MMMM d, yyyy")
                    : "All Upcoming Classes"}
                </h3>
                <Badge variant="outline" className="text-brand-purple border-brand-purple/20 font-body">
                  {visibleClasses.length} class{visibleClasses.length !== 1 ? 'es' : ''}
                </Badge>
              </div>

              {loading ? (
                <div className="space-y-4">{[1, 2, 3].map(i => <div key={i} className="h-32 bg-slate-50 rounded-2xl animate-pulse" />)}</div>
              ) : visibleClasses.length === 0 ? (
                <div className="text-center py-16 bg-slate-50 dark:bg-[#1a1b23] rounded-2xl" data-testid="no-classes-state">
                  <CalendarIcon className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-500 dark:text-slate-400 font-body mb-2">
                    {selectedDate ? "No classes on this date" : "No upcoming classes"}
                  </p>
                  {selectedDate && (
                    <Button size="sm" variant="outline" onClick={() => setSelectedDate(null)}
                      className="rounded-full font-body border-brand-purple text-brand-purple">
                      See all upcoming
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  {groupedByDate.map(([date, dateClasses]) => {
                    const d = parseISO(date);
                    return (
                      <div key={date} data-testid={`date-group-${date}`}>
                        <div className="flex items-baseline gap-2 mb-3 sticky top-16 bg-white dark:bg-[#0f1117] py-2 z-10">
                          <h4 className="font-heading text-lg font-bold text-brand-purple dark:text-brand-purple-light">
                            {format(d, "EEEE, MMM d")}
                          </h4>
                          <span className="text-xs text-slate-400 font-body">
                            {format(d, "yyyy")} · {dateClasses.length} class{dateClasses.length !== 1 ? 'es' : ''}
                          </span>
                        </div>
                        <div className="space-y-3">
                          {dateClasses.map((cls, i) => (
                            <ClassCard key={cls.id} cls={cls} index={i} onBook={() => {
                              trackEvent("begin_checkout", {
                                content_ids: [cls.id],
                                content_name: cls.title,
                                content_category: cls.subject_name || "Class",
                                value: cls.price,
                                currency: "AUD",
                              });
                              navigate(`/book/${cls.id}`);
                            }} />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-slate-50 dark:bg-[#0a0b10] border-t border-slate-100 dark:border-slate-800 py-12 px-6 transition-colors duration-300 mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <p className="font-heading text-lg font-bold text-brand-purple dark:text-brand-purple-light">Tutors Melbourne</p>
          <p className="text-sm text-brand-gold font-body font-semibold">Learning Perfected</p>
          <p className="text-xs text-slate-400 dark:text-slate-500 font-body mt-4">&copy; {new Date().getFullYear()} Tutors Melbourne. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

function ClassCard({ cls, index, onBook }) {
  const spotsLeft = cls.max_occupancy - cls.current_occupancy;
  const isFull = spotsLeft <= 0;
  return (
    <div className="bg-white dark:bg-[#1a1b23] rounded-2xl border border-slate-100 dark:border-slate-800 shadow-[0_4px_20px_rgb(0,0,0,0.03)] card-hover animate-fade-in-up overflow-hidden p-5 transition-colors duration-300"
      style={{ animationDelay: `${index * 0.05}s` }} data-testid={`class-card-${cls.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <Badge className="bg-brand-purple-soft dark:bg-brand-purple/20 text-brand-purple dark:text-brand-purple-light font-body text-xs rounded-full">{cls.subject_name}</Badge>
            {cls.year_level && <Badge variant="outline" className="text-slate-500 dark:text-slate-400 font-body text-xs rounded-full">{cls.year_level}</Badge>}
          </div>
          <h4 className="font-heading text-lg font-semibold text-slate-900 dark:text-white mb-1">{cls.title}</h4>
          {cls.description && <p className="text-sm text-slate-500 dark:text-slate-400 font-body mb-2 line-clamp-2">{cls.description}</p>}
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 dark:text-slate-400 font-body">
            <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{cls.start_time} - {cls.end_time}</span>
            <span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />
              <span className={isFull ? "text-red-500 font-semibold" : spotsLeft <= 3 ? "text-amber-600 font-semibold" : ""}>
                {isFull ? "Full" : `${spotsLeft} spot${spotsLeft !== 1 ? 's' : ''} left`}
              </span>
            </span>
            <span className="flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5" />{cls.tutor_name}</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="font-heading text-2xl font-bold text-brand-purple dark:text-brand-purple-light">${cls.price.toFixed(2)} <span className="text-xs font-body font-semibold text-slate-500 dark:text-slate-400">+ GST</span></p>
            <p className="text-xs text-slate-400 dark:text-slate-500 font-body">AUD per student</p>
          </div>
          <Button onClick={onBook} disabled={isFull}
            className={`rounded-full px-6 font-body font-semibold transition-all duration-300 ${isFull ? 'bg-slate-200 text-slate-400' : 'bg-brand-gold hover:bg-brand-gold-hover text-white shadow-md hover:shadow-lg hover:-translate-y-0.5'}`}
            data-testid={`book-btn-${cls.id}`}>
            {isFull ? "Full" : "Book Now"}
          </Button>
        </div>
      </div>
    </div>
  );
}
